import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  VpnKey,
  ContentCopy,
  Check as CheckIcon,
  Visibility,
  VisibilityOff,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import { StudentCredentials } from '../../types/session.types';

interface EnvironmentPanelProps {
  credentials: StudentCredentials;
}

interface CredentialField {
  label: string;
  value: string;
  isPassword?: boolean;
  isUrl?: boolean;
}

const EnvironmentPanel: React.FC<EnvironmentPanelProps> = ({ credentials }) => {
  const [expanded, setExpanded] = useState(true);
  const [revealedFields, setRevealedFields] = useState<Set<string>>(new Set());
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const fields: CredentialField[] = [
    { label: 'Tenant URL', value: credentials.tenantUrl || '', isUrl: true },
    { label: 'Admin Email', value: credentials.tenantAdminEmail || '' },
    { label: 'Admin Password', value: credentials.tenantAdminPassword || '', isPassword: true },
    { label: 'Skytap Portal', value: credentials.sharingPortalUrl || '', isUrl: true },
    { label: 'Portal Password', value: credentials.skytapPortalPassword || '', isPassword: true },
  ].filter((f) => f.value);

  if (fields.length === 0) return null;

  const handleCopy = (label: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(label);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleReveal = (label: string) => {
    setRevealedFields((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  return (
    <Paper elevation={0} sx={{
      mb: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      overflow: 'hidden',
    }}>
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          bgcolor: '#f0f9ff',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: '#e0f2fe' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKey sx={{ fontSize: 18, color: '#2a7b88' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a5c66' }}>
            My Environment
          </Typography>
        </Box>
        {expanded ? <ExpandLess sx={{ color: '#1a5c66' }} /> : <ExpandMore sx={{ color: '#1a5c66' }} />}
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: 2, py: 1.5 }}>
          {fields.map((field) => {
            const isRevealed = revealedFields.has(field.label);
            const isCopied = copiedField === field.label;

            return (
              <Box key={field.label} sx={{ py: 1, borderBottom: '1px solid', borderColor: 'grey.100', '&:last-child': { borderBottom: 'none' } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                    {field.label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {field.isPassword && (
                      <Tooltip title={isRevealed ? 'Hide' : 'Reveal'}>
                        <IconButton size="small" onClick={() => toggleReveal(field.label)} sx={{ p: 0.5 }}>
                          {isRevealed ? <VisibilityOff sx={{ fontSize: 16 }} /> : <Visibility sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title={isCopied ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={() => handleCopy(field.label, field.value)} sx={{ p: 0.5 }}>
                        {isCopied ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <ContentCopy sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                {field.isUrl ? (
                  <Typography
                    component="a"
                    href={field.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{
                      mt: 0.25,
                      display: 'block',
                      fontFamily: '"Consolas", monospace',
                      wordBreak: 'break-all',
                      fontSize: '0.85rem',
                      color: 'primary.main',
                      textDecoration: 'none',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {field.value}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{
                    mt: 0.25,
                    fontFamily: field.isPassword ? '"Consolas", monospace' : 'inherit',
                    wordBreak: 'break-all',
                    fontSize: '0.85rem',
                  }}>
                    {field.isPassword && !isRevealed ? '\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022' : field.value}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default EnvironmentPanel;
